import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCompare } from '@/contexts/CompareContext';
import api from '@/api/axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, DollarSign, FileText, GitCompare, Download, Shield } from 'lucide-react';

const UserPolicyDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToCompare } = useCompare();
  const navigate = useNavigate();
  
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchPolicy();
    }
  }, [id, user]);

  const fetchPolicy = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/user-policies/${id}`);
      setPolicy(response.data);
    } catch (error) {
      console.error('Failed to fetch policy:', error);
      toast.error('Failed to load policy details');
      navigate('/my-policies');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isExpired = (expiryDate) => {
    return new Date(expiryDate) < new Date();
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const days = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const handleCompare = () => {
    if (policy) {
      addToCompare(policy, true);
      navigate(`/policies?type=${policy.policy_type}`);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-secondary-600 mb-4">Please login to view policy details</p>
            <Button onClick={() => navigate('/login')}>Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8">
          <Skeleton className="h-12 w-1/3 mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Skeleton className="h-96" />
            </div>
            <Skeleton className="h-96" />
          </div>
        </div>
      </div>
    );
  }

  if (!policy) {
    return null;
  }

  const expired = isExpired(policy.expiry_date);
  const daysUntilExpiry = getDaysUntilExpiry(policy.expiry_date);

  return (
    <div className="min-h-screen bg-secondary-50" data-testid="user-policy-detail-page">
      {/* Header */}
      <div className="bg-white border-b border-secondary-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8">
          <Link to="/my-policies">
            <Button variant="ghost" size="sm" className="mb-4" data-testid="back-button">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to My Policies
            </Button>
          </Link>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary">{policy.policy_type}</Badge>
                {expired && (
                  <Badge variant="destructive">Expired</Badge>
                )}
                {!expired && daysUntilExpiry <= 30 && (
                  <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">
                    Expiring in {daysUntilExpiry} days
                  </Badge>
                )}
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold font-heading text-secondary-900 mb-2">
                {policy.insurer_name}
              </h1>
              <p className="text-lg text-secondary-600">Policy No: {policy.policy_number}</p>
            </div>
            <Button size="lg" onClick={handleCompare} data-testid="compare-with-plans-button">
              <GitCompare className="mr-2 h-5 w-5" />
              Compare With Other Plans
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Policy Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-semibold flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary-600" />
                  Policy Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium text-secondary-600 mb-1">Insurance Company</p>
                    <p className="text-base font-semibold text-secondary-900">{policy.insurer_name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-secondary-600 mb-1">Policy Type</p>
                    <p className="text-base font-semibold text-secondary-900">{policy.policy_type} Insurance</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-secondary-600 mb-1">Policy Number</p>
                    <p className="text-base font-semibold text-secondary-900 font-mono">{policy.policy_number}</p>
                  </div>
                  {policy.premium && (
                    <div>
                      <p className="text-sm font-medium text-secondary-600 mb-1">Annual Premium</p>
                      <p className="text-2xl font-bold text-primary-700 font-mono">{formatCurrency(policy.premium)}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Coverage Period */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-semibold flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary-600" />
                  Coverage Period
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium text-secondary-600 mb-1">Start Date</p>
                    <p className="text-base font-semibold text-secondary-900">{formatDate(policy.start_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-secondary-600 mb-1">Expiry Date</p>
                    <p className={`text-base font-semibold ${expired ? 'text-red-600' : 'text-secondary-900'}`}>
                      {formatDate(policy.expiry_date)}
                    </p>
                  </div>
                </div>
                
                {!expired && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                      <strong>Status:</strong> Active for {daysUntilExpiry} more days
                    </p>
                  </div>
                )}
                
                {expired && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-900">
                      <strong>Status:</strong> This policy has expired. Please renew or purchase a new policy.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Additional Notes */}
            {policy.notes && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl font-semibold flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary-600" />
                    Additional Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-base text-secondary-700 leading-relaxed whitespace-pre-wrap">
                    {policy.notes}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Policy Document */}
            {policy.document_url && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl font-semibold flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary-600" />
                    Policy Document
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between bg-secondary-50 p-4 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                        <FileText className="h-6 w-6 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-secondary-900">Policy Document</p>
                        <p className="text-sm text-secondary-600">Uploaded document</p>
                      </div>
                    </div>
                    <a 
                      href={`${process.env.REACT_APP_BACKEND_URL}${policy.document_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="outline" data-testid="download-document-button">
                        <Download className="mr-2 h-4 w-4" />
                        View Document
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="default" className="w-full" onClick={handleCompare} data-testid="compare-sidebar-button">
                  <GitCompare className="mr-2 h-4 w-4" />
                  Compare With Other Plans
                </Button>
                <Link to={`/my-policies`}>
                  <Button variant="outline" className="w-full" data-testid="view-all-button">
                    View All My Policies
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Policy Summary */}
            <Card className="bg-gradient-to-br from-primary-50 to-blue-50 border-2 border-primary-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-primary-900">Policy Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-secondary-700">Type</span>
                  <Badge variant="secondary">{policy.policy_type}</Badge>
                </div>
                {policy.premium && (
                  <div className="flex items-center justify-between py-2 border-t border-primary-200">
                    <span className="text-sm text-secondary-700">Premium</span>
                    <span className="font-bold text-primary-700 font-mono">{formatCurrency(policy.premium)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between py-2 border-t border-primary-200">
                  <span className="text-sm text-secondary-700">Status</span>
                  <Badge variant={expired ? 'destructive' : 'default'} className={!expired && daysUntilExpiry <= 30 ? 'bg-orange-500' : ''}>
                    {expired ? 'Expired' : daysUntilExpiry <= 30 ? 'Expiring Soon' : 'Active'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserPolicyDetailPage;
